import {
  Card,
  CardBody,
  Grid,
  SimpleGrid,
  Stack,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import MetricCard from "./metricCard";
import ProfitCard from "./profitCard";

import { getPlValues } from "../../api/growthPlan";
import { getMetricData, getMetricGraphData } from "../../api/metric";
import { useCurrentProfile } from "../../ContextWrapper";
import {
  colorExpenseFormat,
  colorFormat,
  customCurrencyFormat,
} from "../../utils";
import YtdToggle from "../dashboardComponents/ytdToggle";
import NotificationContainer from "../generic/updateNotification";
import UncategorizedNotificationBanner from "../uncategorized/uncategorizedNotificationBanner";
import MetricExpenseCard from "./metricExpenseCard";
import MetricGraph from "./metricGraph";
import { format } from "date-fns";

const MetricView = ({ customer_id, dashDate }) => {
  const formattedDate = format(dashDate, "yyyy-MM-dd");

  const [metricData, setMetricData] = useState({
    income: "-",
    income_forecast: "-",
    income_forecast_percentage: "-",
    expense: "-",
    expense_forecast: "-",
    expense_forecast_percentage: "-",
    profit: "-",
    cogs: "-",
    cogs_percentage: "-",
    marketing: "-",
    marketing_percentage: "-",
    team: "-",
    team_percentage: "-",
    overhead: "-",
    overhead_percentage: "-",
    profit_percentage: "-",
    ytd_income: "-",
    ytd_expense: "-",
    ytd_income_percentage: "-",
    ytd_expense_percentage: "-",
  });

  const [metricGraphData, setMetricGraphData] = useState({
    income: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    expense: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    profit: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    months: ["-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-"],
  });

  const [pLData, setPLData] = useState({
    overhead_pl: "-",
    team_pl: "-",
    profit_pl: "-",
    marketing_pl: "-",
    cogs_pl: "-",
  });

  const adjusted = JSON.parse(
    sessionStorage.getItem("adjustedView") || "false"
  );

  const [customTarget, setCustomTarget] = useState(
    JSON.parse(localStorage.getItem("isCustomTarget") || "false")
  );

  useEffect(() => {
    const handleTargetTypeChange = (event) => {
      setCustomTarget(event.detail);
    };

    window.addEventListener("targetTypeChanged", handleTargetTypeChange);

    return () => {
      window.removeEventListener("targetTypeChanged", handleTargetTypeChange);
    };
  }, []);

  const [ytd, setYtd] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ytd") || "false");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = () => {
      const ytd = JSON.parse(sessionStorage.getItem("ytd") || "false");
      setYtd(ytd);
    };

    window.addEventListener("ytd-changed", handler);
    return () => window.removeEventListener("ytd-changed", handler);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshMetricData = async (id) => {
      if (!id) return;
      try {
        const res = await getMetricData(id, formattedDate, controller.signal, {
          adjusted,
          ytd,
          customTarget,
        });
        if (isActive) setMetricData(res.data);
      } catch (err) {
        const status = err?.response?.status;
        if (status) {
          console.log("Error status:", status);
        } else {
          console.log("Non-response error (maybe abort):", err?.message || err);
        }
      }
    };

    refreshMetricData(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted, ytd, customTarget]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshMetricGraphData = async (id) => {
      if (!id) return;
      try {
        const res = await getMetricGraphData(
          id,
          formattedDate,
          controller.signal,
          { adjusted }
        );
        if (isActive) setMetricGraphData(res.data);
      } catch (err) {
        if (err?.response) console.log(err);
      }
    };

    refreshMetricGraphData(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshPLData = async (id) => {
      if (!id) return;
      try {
        const res = await getPlValues(id, formattedDate, controller.signal, {
          adjusted,
          ytd,
          customTarget,
        });
        if (isActive) setPLData(res.data);
      } catch (err) {
        if (err?.response) console.log(err);
      }
    };

    refreshPLData(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted, ytd, customTarget]);

  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });
  const [stackingBreak] = useMediaQuery("(max-width: 1023px)", { ssr: false });
  const { profile } = useCurrentProfile();

  return (
    <Stack bg="#F4F4F2" marginBottom={"10px"} mt={isMobile ? "140px" : "75px"}>
      <NotificationContainer rounded={true} p={"14px 15px 0px"} />
      {profile?.type === "client" && (
        <UncategorizedNotificationBanner customerId={customer_id} />
      )}

      <Stack p="18px 15px 0px 15px">
        <YtdToggle></YtdToggle>
      </Stack>

      <Stack p="5px 20px">
        {!stackingBreak && (
          <Grid
            templateColumns="2fr 275px 2fr"
            spacing={2}
            p="10px 0px 10px 0px"
            alignItems="center"
            justifyItems="center"
          >
            <Stack
              width="100%"
              align="center"
              gridColumn={1}
              gridRow={"span 2"}
            >
              <MetricCard
                title="Total Income"
                amount={customCurrencyFormat(
                  metricData ? metricData.income : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_income : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_income_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.income_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.income_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.income_forecast_percentage : "0.00"
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_income_percentage : "0.00"
                )}
                image={"/money-up-arrow.png"}
                type="income"
              />
            </Stack>

            <Stack align="center" gridColumn={2} gridRow="span 2" width="320px">
              <ProfitCard
                profit={customCurrencyFormat(
                  metricData ? metricData.profit_percentage : 0.0,
                  true
                )}
                profitPercentage={customCurrencyFormat(
                  metricData ? metricData.profit : 0.0
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.profit_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.profit_pl : "0.00",
                  metricData ? metricData.profit_percentage : "0.00",
                  true,
                  true
                )}
                status={true}
                data={metricData ? metricData : {}}
              />
            </Stack>
            <Stack
              width="100%"
              align="center"
              gridColumn={3}
              gridRow={"span 2"}
            >
              <MetricCard
                title="Total Expenses"
                amount={customCurrencyFormat(
                  metricData ? metricData.expense : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_expense : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_expense_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.expense_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.expense_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.expense_forecast_percentage : "0.00",
                  true
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_expense_percentage : "0.00",
                  true
                )}
                image={"/money-down-arrow.png"}
                type="expense"
              />
            </Stack>
          </Grid>
        )}
        {stackingBreak && (
          <VStack pt={"10px"}>
            <Stack align="center" gridColumn={2} gridRow="span 2" width="320px">
              <ProfitCard
                profit={customCurrencyFormat(
                  metricData ? metricData.profit_percentage : 0.0,
                  true
                )}
                profitPercentage={customCurrencyFormat(
                  metricData ? metricData.profit : 0.0
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.profit_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.profit_pl : "0.00",
                  metricData ? metricData.profit_percentage : "0.00",
                  true,
                  true
                )}
                status={true}
                data={metricData ? metricData : {}}
              />
            </Stack>

            <Stack
              width="100%"
              align="center"
              gridColumn={1}
              gridRow={"span 2"}
            >
              <MetricCard
                title="Total Income"
                amount={customCurrencyFormat(
                  metricData ? metricData.income : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_income : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_income_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.income_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.income_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.income_forecast_percentage : "0.00"
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_income_percentage : "0.00"
                )}
                image={"/money-up-arrow.png"}
                type="income"
              />
            </Stack>

            <Stack
              width="100%"
              align="center"
              gridColumn={3}
              gridRow={"span 2"}
            >
              <MetricCard
                title="Total Expenses"
                amount={customCurrencyFormat(
                  metricData ? metricData.expense : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_expense : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_expense_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.expense_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.expense_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.expense_forecast_percentage : "0.00",
                  true
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_expense_percentage : "0.00",
                  true
                )}
                image={"/money-down-arrow.png"}
                type="expense"
              />
            </Stack>
          </VStack>
        )}
        <Card
          height="100%"
          maxHeight={"100%"}
          width="100%"
          borderRadius={"12px"}
          marginBottom={"10px"}
        >
          <CardBody height="100%" p={"24px 28px 24px 28px "}>
            <MetricGraph metricGraphData={metricGraphData} />
          </CardBody>
        </Card>
        {!isMobile && (
          <SimpleGrid
            templateColumns="1fr 1fr 1fr 1fr"
            spacing={6}
            maxWidth={"100%"}
          >
            <MetricExpenseCard
              title="Team"
              total={customCurrencyFormat(metricData ? metricData.team : 0.0)}
              percentage={customCurrencyFormat(
                metricData ? metricData.team_percentage : 0.0,
                true,
                false,
                true,
                true
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.team_pl : 0.0,
                true
              )}
              color={colorExpenseFormat(
                pLData ? pLData.team_pl : "0.00",
                metricData ? metricData.team_percentage : "0.00"
              )}
              type="team"
            />
            <MetricExpenseCard
              title="Marketing"
              total={customCurrencyFormat(
                metricData ? metricData.marketing : 0.0
              )}
              percentage={customCurrencyFormat(
                metricData ? metricData.marketing_percentage : 0.0,
                true,
                false,
                true,
                true
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.marketing_pl : 0.0,
                true
              )}
              color={colorExpenseFormat(
                pLData ? pLData.marketing_pl : "0.00",
                metricData ? metricData.marketing_percentage : "0.00"
              )}
              type="marketing"
            />
            <MetricExpenseCard
              title="Operations"
              total={customCurrencyFormat(
                metricData ? metricData.overhead : 0.0
              )}
              percentage={customCurrencyFormat(
                metricData ? metricData.overhead_percentage : 0.0,
                true,
                false,
                true,
                true
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.overhead_pl : 0.0,
                true
              )}
              color={colorExpenseFormat(
                pLData ? pLData.overhead_pl : "0.00",
                metricData ? metricData.overhead_percentage : "0.00"
              )}
              type="overhead"
            />
            <MetricExpenseCard
              title="Products"
              total={customCurrencyFormat(metricData ? metricData.cogs : 0.0)}
              percentage={customCurrencyFormat(
                metricData ? metricData.cogs_percentage : 0.0,
                true,
                false,
                true,
                true
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.cogs_pl : 0.0,
                true
              )}
              color={colorExpenseFormat(
                pLData ? pLData.cogs_pl : "0.00",
                metricData ? metricData.cogs_percentage : "0.00"
              )}
              type="cogs"
            />
          </SimpleGrid>
        )}{" "}
        {isMobile && (
          <VStack width="100%" spacing={4} align="stretch">
            <Grid
              width="100%"
              templateColumns={{
                base: "1fr", // Single column for smaller screens
                md: "repeat(2, 1fr)", // Two columns for medium screens
              }}
              gap={4} // Adds spacing between grid items
            >
              <MetricExpenseCard
                title="Team"
                total={customCurrencyFormat(metricData ? metricData.team : 0.0)}
                percentage={customCurrencyFormat(
                  metricData ? metricData.team_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.team_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.team_pl : "0.00",
                  metricData ? metricData.team_percentage : "0.00"
                )}
                type="team"
              />
              <MetricExpenseCard
                title="Marketing"
                total={customCurrencyFormat(
                  metricData ? metricData.marketing : 0.0
                )}
                percentage={customCurrencyFormat(
                  metricData ? metricData.marketing_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.marketing_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.marketing_pl : "0.00",
                  metricData ? metricData.marketing_percentage : "0.00"
                )}
                type="marketing"
              />
              <MetricExpenseCard
                title="Operations"
                total={customCurrencyFormat(
                  metricData ? metricData.overhead : 0.0
                )}
                percentage={customCurrencyFormat(
                  metricData ? metricData.overhead_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.overhead_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.overhead_pl : "0.00",
                  metricData ? metricData.overhead_percentage : "0.00"
                )}
                type="overhead"
              />
              <MetricExpenseCard
                title="Products"
                total={customCurrencyFormat(metricData ? metricData.cogs : 0.0)}
                percentage={customCurrencyFormat(
                  metricData ? metricData.cogs_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.cogs_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.cogs_pl : "0.00",
                  metricData ? metricData.cogs_percentage : "0.00"
                )}
                type="cogs"
              />
            </Grid>
          </VStack>
        )}
      </Stack>
    </Stack>
  );
};

export default MetricView;
